// eslint-disable-next-line no-unused-vars
class GA4Datalayer {
	constructor(dataLayer) {
		this.dataLayer = dataLayer;
		this.useGlobalId = false;
		this.batchSize = 30;

		this.events = {
			selectItem: {
				selectors: ["[data-layer='select_item']"],
				func: GA4Datalayer.addEvents.bind(this, { action: "click", callback: this.pushSelectItem.bind(this) }),
			},
			viewItemList: {
				selectors: ["[data-layer='view_item_list']"],
				func: this.pushItemList.bind(this),
			},
			viewItemSelected: {
				// One variant items starts pre-selected
				selectors: ["#variant-selector"],
				func: this.pushViewItem.bind(this, "#variant-selector"),
			},
			viewItemUpdate: {
				// Multiple variants
				selectors: ["#variant-options-form"],
				func: GA4Datalayer.addEvents.bind(this, {
					action: "change",
					callback: this.pushViewItem.bind(this, "#variant-selector"),
				}),
			},
			beginCart: {
				selectors: ["#add-to-bag"],
				func: GA4Datalayer.addEvents.bind(this, {
					action: "click",
					callback: this.pushBeginCart.bind(this),
				}),
			},
			beginGiftBuilder: {
				selectors: ["#add-to-bag"],
				func: GA4Datalayer.addEvents.bind(this, {
					action: "click",
					callback: this.pushBeginGiftBuilder.bind(this),
				}),
			},
			viewCart: {
				selectors: ["#cart-drawer-toggle"],
				func: GA4Datalayer.addEvents.bind(this, {
					action: "click",
					callback: this.pushViewCart.bind(this),
				}),
			},
		};

		// Override default push() to always include experiments
		this.decoratePush();

		this.init();
	}

	decoratePush() {
		const originalPush = this.dataLayer.push.bind(this.dataLayer);

		this.dataLayer.push = (payload) => {
			if (payload && typeof payload === "object" && payload.event) {
				const experiments = this.getExperimentsFromCookie();

				if (Object.keys(experiments).length > 0) {
					// Object with all experiments
					payload.experiments = experiments;

					// String format(allows add only one dimension on GA4, but then more work on GA4 exploration)
					const expString = Object.keys(experiments)
						.map((key) => {
							const cleanKey = key.replace(/[^a-zA-Z0-9]/g, "_");
							return `${cleanKey}:${experiments[key]}`;
						})
						.join("|");

					payload.experiments_string = expString;

					// Flatten dynamically for GA4( need a dimension for each exp)
					Object.keys(experiments).forEach((key) => {
						const cleanKey = key.replace(/[^a-zA-Z0-9]/g, "_");
						payload[`exp_${cleanKey}`] = experiments[key];
					});
				}
			}

			return originalPush(payload);
		};
	}

	getExperimentsFromCookie() {
		const match = document.cookie.match(/(^| )ab_exp=([^;]+)/);
		if (!match) return {};

		try {
			const parsed = JSON.parse(decodeURIComponent(match[2]));
			const result = {};

			Object.values(parsed).forEach((entry) => {
				const [exp, variant] = entry.split(":");
				if (exp && variant) {
					result[exp] = variant;
				}
			});

			return result;
		} catch (e) {
			return {};
		}
	}

	static addEvents(event, targets) {
		targets.forEach((target) => {
			target.addEventListener(event.action, event.callback);
		});
	}

	static arraySplit(items, chunkSize) {
		if (!chunkSize || chunkSize <= 0) return [items];
		const chunks = [];
		for (let i = 0; i < items.length; i += chunkSize) {
			chunks.push(items.slice(i, i + chunkSize));
		}
		return chunks;
	}

	formatItemId(globalId) {
		if (this.useGlobalId) return globalId;
		return globalId.replace(/\D+/g, "");
	}

	pushLogState(customerId) {
		// User ID tracking
		this.dataLayer.push({
			logState: customerId ? "Logged In" : "Logged Out",
			...(customerId && { userId: this.formatItemId(customerId) }),
		});
	}

	pushItemList(listNodes) {
		if (!Array.isArray(listNodes) || listNodes.length === 0) return;

		listNodes.forEach((list) => {
			const listItems = Array.from(list.querySelectorAll("article"));

			// Remove items like collections from productSliderRow
			const filteredItems = listItems.filter((item) => item.getAttribute("data-product"));

			// send in chunks
			GA4Datalayer.arraySplit(filteredItems, this.batchSize).forEach((items) => {
				const payload = [];
				items.forEach((item) => {
					const data = JSON.parse(item.getAttribute("data-product") || null);
					if (data) {
						payload.push({
							sku: data.sku,
							item_id: this.formatItemId(data.id),
							item_name: data.title,
							item_category: data.category,
							item_variant: data.variant,
							item_list_name: data.list,
							price: data.price,
							index: data.position,
							quantity: 1,
						});
					}
				});

				this.dataLayer.push({ ecommerce: null });
				this.dataLayer.push({
					event: "view_item_list",
					ecommerce: {
						currency: "GBP",
						items: payload,
					},
				});
			});
		});
	}

	pushSelectItem({ currentTarget }) {
		if (!currentTarget) return;

		const data = JSON.parse(currentTarget.getAttribute("data-product") || null);

		if (!data) return;

		this.dataLayer.push({ ecommerce: null });
		this.dataLayer.push({
			event: "select_item",
			ecommerce: {
				currency: "GBP",
				value: data.price,
				items: [
					{
						sku: data.sku,
						item_id: this.formatItemId(data.id),
						item_name: data.title,
						item_category: data.category,
						item_variant: data.variant,
						item_list_name: data.list,
						price: data.price,
						index: data.position,
						quantity: 1,
					},
				],
			},
		});
	}

	pushBeginCart() {
		// It actually should be begin gift builder
		// Keeping as it is for compatibility
		this.dataLayer.push({
			event: "begin_cart",
		});
	}

	pushBeginGiftBuilder() {
		// New event, not mapped on GTM
		this.dataLayer.push({
			event: "begin_gift_builder",
		});
	}

	pushViewItem(variantFormId) {
		const variantForm = document.querySelector(variantFormId);
		const selectedOption = variantForm.querySelector("input:checked");

		if (!selectedOption) return;

		const product = document.querySelector("#pdp[data-product]");
		const productData = JSON.parse(product.getAttribute("data-product") || null);
		if (!productData) return;

		this.dataLayer.push({ ecommerce: null });
		this.dataLayer.push({
			event: "view_item",
			ecommerce: {
				currency: productData.currency,
				value: parseFloat(selectedOption.getAttribute("data-price")),
				items: [
					{
						item_category: productData.category,
						item_id: this.formatItemId(productData.id),
						item_name: productData.title,
						item_variant: selectedOption.getAttribute("data-title"),
						sku: selectedOption.getAttribute("data-sku"),
						price: parseFloat(selectedOption.getAttribute("data-price")),
						quantity: 1,
					},
				],
			},
		});
	}

	pushViewCart() {
		const cartDrawer = document.getElementById("cart-drawer");
		if (!cartDrawer || cartDrawer.getAttribute("aria-expanded") !== "true") return;

		const items =
			window.cart?.cartObject?.lines?.nodes.map((line) => ({
				item_category: line.merchandise.product.category,
				item_id: this.formatItemId(line.merchandise.id),
				item_name: line.merchandise.product.title,
				item_variant: line.merchandise.title,
				sku: line.merchandise.sku,
				price: line.merchandise.price.amount,
				quantity: line.quantity,
			})) || [];

		this.dataLayer.push({ ecommerce: null });
		this.dataLayer.push({
			event: "view_cart",
			ecommerce: {
				currency: "GBP",
				value: window.cart?.subtotal || 0,
				items,
			},
		});
	}

	pushItems(nodes, eventName) {
		const payload = {
			event: eventName,
			ecommerce: {
				currency: "GBP",
				value: 0,
				items: [],
			},
		};

		nodes
			.filter((item) => Object.keys(item).length !== 0) // remove empty objects
			.forEach((node) => {
				const price = parseFloat(node.merchandise.price.amount);
				if (node.merchandise.id) {
					payload.ecommerce.items.push({
						item_name: node.merchandise.product.title,
						item_id: this.formatItemId(node.merchandise.product.id),
						item_category: node.merchandise.product.productType,
						item_variant: node.merchandise.title,
						sku: node.merchandise.sku,
						quantity: node.quantity,
						price,
					});
					payload.ecommerce.value += price;
				}
			});

		this.dataLayer.push({ ecommerce: null });
		this.dataLayer.push(payload);
	}

	init() {
		Object.values(this.events).forEach(({ selectors, func }) => {
			const listNodes = [];
			selectors.forEach((selector) => {
				listNodes.push(...(Array.from(document.querySelectorAll(selector)) || []));
			});

			if (listNodes && listNodes.length > 0) func(listNodes);
		});

		// Listen for cart events
		window.addEventListener("cart:addItems", ({ detail }) => {
			this.pushItems(detail, "add_to_cart");
		});
		window.addEventListener("cart:removeItems", ({ detail }) => {
			this.pushItems(detail, "remove_from_cart");
		});
	}
}

window.addEventListener("load", () => {
	if (window.dataLayer) {
		window.GA4DataLayer = new GA4Datalayer(window.dataLayer);
	} else {
		console.warn("GA4Datalayer tracker not loaded");
	}
});
